import { navigate } from "gatsby"
import React, { Component } from "react"
import { connect } from "react-redux"
import packageJson from "../../../package.json"
import LogoGoogle from "../../images/logo_google.png"
import {
  Age,
  CookFrequency,
  FamilyMembers,
  Gender,
  ProfessionSector,
} from "../../models/User"
import {
  createAccount,
  createAccountEnterprise,
  createUserDetails,
  getUserInfoListener,
  handleAuthCondition,
  resetPassword,
  signIn,
  signInWithGoogle,
} from "../../store/actions/authActions"
import { authStateInterface } from "../../store/reducers/authReducer"
import { promotionStateInterface } from "../../store/reducers/promotionReducer"
import { utilsStateInterface } from "../../store/reducers/utilsReducer"
import { utilPhonePlaceholder } from "../../utils/region"
import Button from "../base/Button"
import Input from "../base/Input"
import Overlay from "../base/Overlay"
import Select, { Option } from "../base/Select"
import Steps from "../base/Steps"
import UploadInput from "../base/UploadInput"

export enum AuthCardType {
  SIGNIN = "signIn",
  SIGNUP = "signUp",
  SIGNUPENTER = "signUpEnterprise",
  SIGNUPOPTIONS = "signUpOptions",
  RESET = "resetPassword",
}

interface Props {
  dispatch: any
  type: AuthCardType
  promotionStore: promotionStateInterface
  utilsStore: utilsStateInterface
  authStore: authStateInterface
  handleChangePage?: (page: AuthCardType) => void
  handleFirstSignUp?: () => void
}

const steps = [
  {
    title: "Profile Information",
    description: "Help us to understand you better",
  },
  {
    title: "Create Account",
    description: "Place your security credentials",
  },
  {
    title: "Verification",
    description: "Verify your email to ensure that you are contactable",
  },
]

export const tngUserTypeOptions: Option[] = [
  { key: "Household", title: "Household", label: "Household" },
  { key: "Business", title: "Business", label: "Business" },
]

export const tngStateOptions: Option[] = [
  { key: "Klang", title: "Klang Valley", label: "Klang Valley" },
  { key: "Sembilan", title: "Sembilan", label: "Sembilan" },
  { key: "Penang", title: "Penang", label: "Penang" },
]

export const genderOptions: Option[] = Object.keys(Gender).map(
  eachGenderKey => {
    return {
      key: eachGenderKey,
      title: Gender[eachGenderKey as keyof typeof Gender],
      label: Gender[eachGenderKey as keyof typeof Gender],
    }
  }
)

export const ageOption: Option[] = Object.keys(Age).map(eachAgeKey => {
  return {
    key: eachAgeKey,
    title: Age[eachAgeKey as keyof typeof Age],
    label: Age[eachAgeKey as keyof typeof Age],
  }
})

export const professionOptions: Option[] = Object.keys(ProfessionSector).map(
  eachProfessionKey => {
    return {
      key: eachProfessionKey,
      title:
        ProfessionSector[eachProfessionKey as keyof typeof ProfessionSector],
      label:
        ProfessionSector[eachProfessionKey as keyof typeof ProfessionSector],
    }
  }
)

export const familyMembersOption: Option[] = Object.keys(FamilyMembers).map(
  eachMemberKey => {
    return {
      key: eachMemberKey,
      title: FamilyMembers[eachMemberKey as keyof typeof FamilyMembers],
      label: FamilyMembers[eachMemberKey as keyof typeof FamilyMembers],
    }
  }
)

export const cookFrequencyOption: Option[] = Object.keys(CookFrequency).map(
  eachCookFreqKey => {
    return {
      key: eachCookFreqKey,
      title: CookFrequency[eachCookFreqKey as keyof typeof CookFrequency],
      label: CookFrequency[eachCookFreqKey as keyof typeof CookFrequency],
    }
  }
)

export interface UserState {
  email: string
  password: string
  name: string
  mobileNo: string
  gender: keyof typeof Gender | ""
  age: keyof typeof Age | ""
  profession: keyof typeof ProfessionSector | ""
  professionString: string
  familyMembers: keyof typeof FamilyMembers | ""
  cookFrequency: keyof typeof CookFrequency | ""
  enterprise: File | ""
  tngUserType?: string
  tngState?: string
}

export interface UserStateError {
  emailError: string
  passwordError: string
  nameError: string
  mobileNoError: string
  genderError: string
  ageError: string
  professionError: string
  professionStringError: string
  familyMembersError: string
  cookFrequencyError: string
  enterpriseError: string
}

interface State {
  loading: boolean
  signUpIndex: number
  userState: UserState
  userStateError: UserStateError
}

const originalState = {
  loading: false,
  signUpIndex: 0,
  userState: {
    email: "",
    password: "",
    name: "",
    mobileNo: "",
    gender: "",
    age: "",
    profession: "",
    professionString: "",
    familyMembers: "",
    cookFrequency: "",
  },
  userStateError: {
    emailError: "",
    passwordError: "",
    nameError: "",
    mobileNoError: "",
    genderError: "",
    ageError: "",
    professionError: "",
    professionStringError: "",
    familyMembersError: "",
    cookFrequencyError: "",
  },
}

class AuthCard extends Component<Props> {
  state: State = JSON.parse(JSON.stringify(originalState))

  componentDidUpdate(prevProps: Props) {
    if (prevProps.type !== this.props.type) {
      this.setState(JSON.parse(JSON.stringify(originalState)))
    }
  }

  handleLoading = (loading: boolean) => {
    this.setState({
      loading,
    })
  }

  handleInputChange = (e: any) => {
    const clonedUserState = this.handleCloneUserState()
    clonedUserState[e.target.id] = e.target.value
    this.setState({
      userState: clonedUserState,
    })
  }

  handleSelectChange = (id: string, key: string) => {
    const clonedUserState = this.handleCloneUserState()
    clonedUserState[id] = key
    if (id === "profession") {
      clonedUserState["professionString"] = ""
    }
    this.setState({
      userState: clonedUserState,
    })
  }

  handleUploadChange = (e: any) => {
    const clonedUserState = this.handleCloneUserState()
    clonedUserState["enterprise"] = e.target.files[0]
    this.setState({
      userState: clonedUserState,
    })
  }

  handleCloneUserState = () => {
    const currentEnterprise = this.state.userState.enterprise
    const clonedUserState = JSON.parse(JSON.stringify(this.state.userState))
    clonedUserState["enterprise"] = currentEnterprise
    return clonedUserState
  }

  handleSubmit = async () => {
    let conditionsList: string[] = []

    if (
      this.props.type === AuthCardType.SIGNUP ||
      this.props.type === AuthCardType.SIGNUPENTER
    ) {
      if (this.state.signUpIndex === 0) {
        conditionsList = [
          "name",
          "mobileNo",
          // "gender",
          // "age",
          // "profession",
          // "professionString",
        ]
        if (this.props.type === AuthCardType.SIGNUP) {
          // conditionsList.push("familyMembers")
          // conditionsList.push("cookFrequency")
        } else {
          conditionsList.push("enterprise")
        }
      } else if (this.state.signUpIndex === 1) {
        conditionsList = ["email", "password"]
      }
    } else if (this.props.type === AuthCardType.SIGNIN) {
      conditionsList = ["email", "password"]
    } else if (this.props.type === AuthCardType.RESET) {
      conditionsList = ["email"]
    }

    const clonedUserError = JSON.parse(
      JSON.stringify(this.state.userStateError)
    )
    handleAuthCondition(this.state.userState, clonedUserError, conditionsList)

    this.setState(
      {
        userStateError: clonedUserError,
      },
      async () => {
        if (
          this.props.type === AuthCardType.SIGNUPOPTIONS &&
          this.props.handleChangePage
        ) {
          this.props.handleChangePage(AuthCardType.SIGNUP)
        } else if (
          this.props.type === AuthCardType.SIGNUP ||
          this.props.type === AuthCardType.SIGNUPENTER
        ) {
          if (this.state.signUpIndex === 0) {
            if (
              !this.state.userStateError.nameError &&
              !this.state.userStateError.mobileNoError &&
              !this.state.userStateError.genderError &&
              !this.state.userStateError.ageError &&
              !this.state.userStateError.professionError &&
              !this.state.userStateError.familyMembersError &&
              !this.state.userStateError.cookFrequencyError &&
              !this.state.userStateError.professionStringError &&
              !this.state.userStateError.enterpriseError
            ) {
              const hasAuthToken = this.props.authStore.userAuth ? true : false
              if (hasAuthToken) {
                this.handleLoading(true)
                const promoCode = this.props.promotionStore.promotionCode
                  ? this.props.promotionStore.promotionCode
                  : ""
                const referralId = this.props.promotionStore.referralId ?? ""
                let creationError = await createUserDetails(
                  this.state.userState,
                  promoCode,
                  referralId,
                  this.props.utilsStore.firebase
                )
                if (creationError) {
                  const creationUserError = JSON.parse(
                    JSON.stringify(this.state.userStateError)
                  )
                  creationUserError["nameError"] = creationError
                  this.setState({
                    userStateError: creationUserError,
                  })
                  this.handleLoading(false)
                } else {
                  getUserInfoListener(
                    this.props.dispatch,
                    this.props.utilsStore.tngAccess,
                    this.props.utilsStore.firebase
                  )
                }
              } else {
                this.setState({
                  signUpIndex: 1,
                })
                if (this.props.handleFirstSignUp) {
                  this.props.handleFirstSignUp()
                }
              }
            }
          } else if (this.state.signUpIndex === 1) {
            if (
              !this.state.userStateError.emailError &&
              !this.state.userStateError.passwordError
            ) {
              this.handleLoading(true)
              const promoCode = this.props.promotionStore.promotionCode
                ? this.props.promotionStore.promotionCode
                : ""
              const referralId = this.props.promotionStore.referralId ?? ""
              let creationError = ""
              if (this.props.type === AuthCardType.SIGNUP) {
                creationError = await createAccount(
                  this.state.userState,
                  promoCode,
                  referralId,
                  this.props.utilsStore.firebase
                )
              } else {
                creationError = await createAccountEnterprise(
                  this.state.userState,
                  promoCode,
                  this.props.utilsStore.firebase
                )
              }

              if (creationError) {
                const creationUserError = JSON.parse(
                  JSON.stringify(this.state.userStateError)
                )
                creationUserError["emailError"] = creationError
                this.setState({
                  userStateError: creationUserError,
                })
              } else {
                this.setState({
                  signUpIndex: 2,
                })
              }
              this.handleLoading(false)
            }
          } else if (this.state.signUpIndex === 2) {
            navigate(
              this.props.utilsStore.tngAccess ? "/tngHome" : "/dashboard"
            )
          }
        } else if (this.props.type === AuthCardType.SIGNIN) {
          if (
            !this.state.userStateError.emailError &&
            !this.state.userStateError.passwordError
          ) {
            this.handleLoading(true)
            let signInError = await signIn(
              {
                email: this.state.userState.email,
                password: this.state.userState.password,
              },
              this.props.utilsStore.firebase
            )
            if (signInError) {
              const signInUserError = JSON.parse(
                JSON.stringify(this.state.userStateError)
              )
              signInUserError["emailError"] = signInError
              this.setState({
                userStateError: signInUserError,
              })
            } else {
              this.setState({
                signUpIndex: 2,
              })
            }
            this.handleLoading(false)
          }
        } else if (this.props.type === AuthCardType.RESET) {
          if (!this.state.userStateError.emailError) {
            this.handleLoading(true)
            let resetPasswordError = await resetPassword(
              this.state.userState.email,
              this.props.utilsStore.firebase
            )
            const resetUserError = JSON.parse(
              JSON.stringify(this.state.userStateError)
            )
            if (resetPasswordError) {
              resetUserError["emailError"] = resetPasswordError
            } else {
              resetUserError["emailError"] =
                "Password reset link has been sent to your email"
            }
            this.setState({
              userStateError: resetUserError,
            })
            this.handleLoading(false)
          }
        }
      }
    )
  }

  handleSignInWithGoogle = async () => {
    this.handleLoading(true)
    await signInWithGoogle(
      this.props.utilsStore.firebase,
      this.props.utilsStore.googleAuthProvider
    )
    this.handleLoading(false)
  }

  renderLeftContent = () => {
    if (
      this.props.type === AuthCardType.SIGNIN ||
      this.props.type === AuthCardType.RESET
    ) {
      return (
        <>
          <h3 className="text-lg font-medium text-white">
            Contact information
          </h3>
          <dl className="mt-8 space-y-6">
            <dt>
              <span className="sr-only">Phone number</span>
            </dt>
            <dd className="flex text-base text-green-50">
              <svg
                className="flex-shrink-0 w-6 h-6 text-green-200"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span className="ml-3">+(60) 13-731 1007</span>
            </dd>
            <dt>
              <span className="sr-only">Email</span>
            </dt>
            <dd className="flex text-base text-green-50">
              <svg
                className="flex-shrink-0 w-6 h-6 text-green-200"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span className="ml-3">info@arusoil.com</span>
            </dd>
          </dl>
        </>
      )
    } else {
      return (
        <>
          <h3 className="text-lg font-medium text-white">
            Create Your Account
          </h3>
          <Steps steps={steps} stepIndex={this.state.signUpIndex} />
        </>
      )
    }
  }

  renderSignIn = () => {
    return (
      <>
        <div>
          <label className="block text-sm font-medium text-warm-gray-900">
            Email
          </label>
          <Input
            id="email"
            className="mt-1"
            placeholder="Enter your email"
            value={this.state.userState.email}
            error={this.state.userStateError.emailError}
            onChange={this.handleInputChange}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-warm-gray-900">
            Password
          </label>
          <Input
            id="password"
            type="password"
            className="mt-1"
            placeholder="Enter your password"
            value={this.state.userState.password}
            error={this.state.userStateError.passwordError}
            onChange={this.handleInputChange}
          />
        </div>
      </>
    )
  }

  renderSignUp = () => {
    if (this.state.signUpIndex === 0) {
      return (
        <>
          <div>
            <label className="block text-sm font-medium text-warm-gray-900">
              Name
            </label>
            <Input
              id="name"
              className="mt-1"
              placeholder="Enter your name"
              onChange={this.handleInputChange}
              value={this.state.userState.name}
              error={this.state.userStateError.nameError}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-warm-gray-900">
              Phone Number
            </label>
            <Input
              id="mobileNo"
              className="mt-1"
              placeholder={utilPhonePlaceholder()}
              value={this.state.userState.mobileNo}
              onChange={this.handleInputChange}
              error={this.state.userStateError.mobileNoError}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-warm-gray-900">
              Gender
            </label>
            <Select
              id="gender"
              placeholder="Optional - Select your gender"
              value={this.state.userState.gender}
              options={genderOptions}
              onChange={this.handleSelectChange}
              error={this.state.userStateError.genderError}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-warm-gray-900">
              Age
            </label>
            <Select
              id="age"
              placeholder="Optional - Select your age range"
              value={this.state.userState.age}
              options={ageOption}
              onChange={this.handleSelectChange}
              error={this.state.userStateError.ageError}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-warm-gray-900">
              Profession Sector
            </label>
            <Select
              id="profession"
              placeholder="Optional - Select your profession sector"
              value={this.state.userState.profession}
              options={professionOptions}
              onChange={this.handleSelectChange}
              error={this.state.userStateError.professionError}
            />
          </div>
          {this.state.userState.profession === "OTHERS" && (
            <div>
              <label className="block text-sm font-medium text-warm-gray-900">
                Profession
              </label>
              <Input
                id="professionString"
                className="mt-1"
                placeholder="Optional - Others"
                value={this.state.userState.professionString}
                onChange={this.handleInputChange}
                error={this.state.userStateError.professionStringError}
              />
            </div>
          )}
          {this.props.type === AuthCardType.SIGNUP ? (
            <>
              <div>
                <label className="block text-sm font-medium text-warm-gray-900">
                  Number of your Family Members
                </label>
                <Select
                  id="familyMembers"
                  placeholder="Optional - Number of your family members"
                  value={this.state.userState.familyMembers}
                  options={familyMembersOption}
                  onChange={this.handleSelectChange}
                  error={this.state.userStateError.familyMembersError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-warm-gray-900">
                  How Often Do You Cook at Home?
                </label>
                <Select
                  id="cookFrequency"
                  placeholder="Optional - Cooking Frequency"
                  value={this.state.userState.cookFrequency}
                  options={cookFrequencyOption}
                  onChange={this.handleSelectChange}
                  error={this.state.userStateError.cookFrequencyError}
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-warm-gray-900">
                Company SSM
              </label>
              <UploadInput
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={this.handleUploadChange}
                hidePreview={true}
                file={this.state.userState.enterprise}
                fileDescription="PNG, JPG, JPEG, PDF up to 5MB"
                error={this.state.userStateError.enterpriseError}
              />
            </div>
          )}
          {this.props.type === AuthCardType.SIGNUP && (
            <div className="hidden sm:block" />
          )}
        </>
      )
    } else if (this.state.signUpIndex === 1) {
      return this.renderSignIn()
    } else if (this.state.signUpIndex === 2) {
      return (
        <>
          <label className="block text-sm font-medium text-warm-gray-900">
            Verification link has been sent to your email!
          </label>
          <div className="hidden sm:block" />
        </>
      )
    }
  }

  renderResetPassword = () => {
    return (
      <div>
        <label className="block text-sm font-medium text-warm-gray-900">
          Email
        </label>
        <Input
          id="email"
          className="mt-1"
          placeholder="Enter your email"
          value={this.state.userState.email}
          error={this.state.userStateError.emailError}
          onChange={this.handleInputChange}
        />
      </div>
    )
  }

  renderSignUpSteps = () => {
    return (
      <h3 className="text-lg font-medium text-warm-gray-900">
        {this.props.type === AuthCardType.SIGNUPOPTIONS
          ? "Choose A Sign Up Option"
          : steps[this.state.signUpIndex].title}
      </h3>
    )
  }

  renderAuthButtons = () => {
    let text = "Sign Up"
    let rememberText = ""
    let destination = AuthCardType.RESET

    if (this.props.type === AuthCardType.SIGNUPOPTIONS) {
      text = "Sign Up With Email"
    } else if (
      (this.props.type === AuthCardType.SIGNUP ||
        this.props.type === AuthCardType.SIGNUPENTER) &&
      this.state.signUpIndex === 2
    ) {
      text = "Go To Dashboard"
    } else if (this.props.type === AuthCardType.SIGNIN) {
      text = "Sign In"
      rememberText = "Forgot Your Password?"
      destination = AuthCardType.RESET
    } else if (this.props.type === AuthCardType.RESET) {
      text = "Reset Password"
      rememberText = "Remembered Your Password?"
      destination = AuthCardType.SIGNIN
    }

    return (
      <>
        {rememberText && (
          <div className="sm:col-span-2 sm:flex sm:justify-end">
            <span
              className="text-base text-arusgreen-600 cursor-pointer"
              onClick={this.props.handleChangePage?.bind(this, destination)}
            >
              {rememberText}
            </span>
          </div>
        )}
        <div className="hidden sm:block" />
        <Button
          className="w-full"
          text={text}
          type="normal"
          onClick={this.handleSubmit}
        ></Button>
      </>
    )
  }

  renderGoogleButton = () => {
    let text = ""

    if (this.props.type === AuthCardType.SIGNIN) {
      text = "Sign In With Google"
    } else if (this.props.type === AuthCardType.SIGNUPOPTIONS) {
      text = "Sign Up With Google"
    }

    return (
      <>
        <div className="hidden sm:block" />
        <div className="sm:col-span-1 sm:flex sm:justify-end">
          <Button
            className="w-full"
            text={text}
            type="google"
            onClick={this.handleSignInWithGoogle}
            icon={<img src={LogoGoogle} alt="" className="h-6 w-6 mr-3" />}
          />
        </div>
      </>
    )
  }

  renderSvg = () => {
    return (
      <>
        <div
          className="absolute inset-0 pointer-events-none sm:hidden"
          aria-hidden="true"
        >
          <svg
            className="absolute inset-0 w-full h-full"
            width="343"
            height="388"
            viewBox="0 0 343 388"
            fill="none"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M-99 461.107L608.107-246l707.103 707.107-707.103 707.103L-99 461.107z"
              fill="url(#linear1)"
              fillOpacity=".1"
            />
            <defs>
              <linearGradient
                id="linear1"
                x1="254.553"
                y1="107.554"
                x2="961.66"
                y2="814.66"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#fff"></stop>
                <stop offset="1" stopColor="#fff" stopOpacity="0"></stop>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div
          className="hidden absolute top-0 right-0 bottom-0 w-1/2 pointer-events-none sm:block lg:hidden"
          aria-hidden="true"
        >
          <svg
            className="absolute inset-0 w-full h-full"
            width="359"
            height="339"
            viewBox="0 0 359 339"
            fill="none"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M-161 382.107L546.107-325l707.103 707.107-707.103 707.103L-161 382.107z"
              fill="url(#linear2)"
              fillOpacity=".1"
            />
            <defs>
              <linearGradient
                id="linear2"
                x1="192.553"
                y1="28.553"
                x2="899.66"
                y2="735.66"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#fff"></stop>
                <stop offset="1" stopColor="#fff" stopOpacity="0"></stop>
              </linearGradient>
            </defs>
          </svg>
        </div>
      </>
    )
  }

  renderContent = () => {
    if (this.props.type === AuthCardType.SIGNIN) {
      return this.renderSignIn()
    } else if (
      this.props.type === AuthCardType.SIGNUP ||
      this.props.type === AuthCardType.SIGNUPENTER
    ) {
      return this.renderSignUp()
    } else if (this.props.type === AuthCardType.RESET) {
      return this.renderResetPassword()
    }
  }

  render() {
    return (
      <>
        <Overlay
          loading={this.state.loading}
          text="Please hold on while we are verifying your account"
        />
        <div className="relative bg-white shadow-xl">
          <h2 id="contactHeading" className="sr-only">
            {this.props.type === AuthCardType.SIGNIN
              ? "Contact Us"
              : "Create Your Account"}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3">
            <div className="relative overflow-hidden py-10 px-6 bg-gradient-to-b from-arusgreen-500 to-arusgreen-600 sm:px-10 xl:p-12">
              {this.renderSvg()}
              {this.renderLeftContent()}
            </div>

            <div className="py-10 px-6 sm:px-10 lg:col-span-2 xl:p-12">
              {(this.props.type === AuthCardType.SIGNUP ||
                this.props.type === AuthCardType.SIGNUPENTER ||
                this.props.type === AuthCardType.SIGNUPOPTIONS) &&
                this.renderSignUpSteps()}
              <form
                action="#"
                method="POST"
                className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
              >
                {this.renderContent()}
                {this.renderAuthButtons()}
                {(this.props.type === AuthCardType.SIGNIN ||
                  this.props.type === AuthCardType.SIGNUPOPTIONS) && (
                  <>
                    <div className="flex flex-row items-center sm:col-span-2">
                      <div className="flex-grow border-t border-gray-300"></div>
                      <span className="text-sm px-2 text-gray-500">
                        Or Continue With
                      </span>
                      <div className="flex-grow border-t border-gray-300"></div>
                    </div>
                    {this.renderGoogleButton()}
                  </>
                )}
                <div className="sm:col-span-2 sm:flex sm:justify-end">
                  <span className="text-xs text-gray-500">
                    Version {packageJson.version}
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </>
    )
  }
}

const mapStateToProps = (state: any) => {
  return {
    authStore: state.authStore,
    utilsStore: state.utilsStore,
    promotionStore: state.promotionStore,
  }
}

export default connect(mapStateToProps)(AuthCard)
